/**
 * tel:, mailto:, webcal: (and sms:, whatsapp:) links inside the Android app.
 *
 * From 1.9.5 to 1.9.8 the Android shell drops these taps. Its WebView only lets https and about:
 * through to the shell's own link handler; anything else goes to Linking.canOpenURL, which on
 * Android 11+ answers false for every scheme the app did not declare in its manifest. So «Llama
 * al 112» in Servicios, every «Llamar» in Farmacias and Supermercados, and every mailto: did
 * nothing. The 1.9.9 shell fixes it (mobile/CRITICAL_FIXES.md #34), but a shell update takes days
 * of store review and then as long as each person takes to update.
 *
 * The shell's OPEN_EXTERNAL message has handed tel:/mailto:/webcal: to the OS since 1.9.1, so the
 * web sends these taps there instead of letting the WebView try to navigate. That repairs every
 * Android shell from 1.9.1 on the day this deploys.
 *
 * iOS is left alone: there the same taps go through canOpenURL, which works because app.json
 * lists these schemes in LSApplicationQueriesSchemes. A browser is left alone as well.
 */
import { appPlatform, appVersion, compareVersions } from './nativeApp';

const SCHEME = /^(tel|mailto|sms|whatsapp|webcal):/i;

/**
 * Should a tap on `href` go to the shell as OPEN_EXTERNAL rather than to the WebView?
 * `platform` and `version` are what lib/nativeApp.js reports (null in a browser).
 */
export function routesThroughShell(href, { platform, version }) {
  const m = SCHEME.exec(String(href || '').trim());
  if (!m || platform !== 'android' || !version) return false;
  // Before 1.9.1 there is no OPEN_EXTERNAL at all: the message would be ignored.
  if (compareVersions(version, '1.9.1') < 0) return false;
  const scheme = m[1].toLowerCase();
  if (scheme === 'sms' || scheme === 'whatsapp') {
    // 1.9.1–1.9.4 open any URL; 1.9.5–1.9.8 refuse these two; 1.9.9 accepts them again. (1.9.9 was
    // never released with the narrow list: the store app before it is 1.9.8.)
    return compareVersions(version, '1.9.5') < 0 || compareVersions(version, '1.9.9') >= 0;
  }
  return true;
}

/**
 * One capture-phase listener for the whole document, like installMapLinkInterceptor: the links
 * stay ordinary links (focusable, announced as links), and ones added later are covered too.
 * Installed whenever the page is inside the shell; the platform and version are read at tap time,
 * because older shells only set them after the page has loaded.
 */
export function installExternalLinkInterceptor() {
  if (typeof document === 'undefined' || typeof window === 'undefined') return;
  if (!window.ReactNativeWebView || window.__externalLinksIntercepted) return;
  window.__externalLinksIntercepted = true;

  document.addEventListener('click', (e) => {
    if (e.defaultPrevented) return;
    const a = e.target?.closest?.('a[href]');
    if (!a) return;
    const href = (a.getAttribute('href') || '').trim();
    if (!routesThroughShell(href, { platform: appPlatform(), version: appVersion() })) return;
    const bridge = window.ReactNativeWebView;
    if (!bridge?.postMessage) return;
    e.preventDefault();
    bridge.postMessage(JSON.stringify({ type: 'OPEN_EXTERNAL', url: href }));
  }, true);
}
